import { createMuiTheme, Theme } from '@material-ui/core'
import createBreakpoints from '@material-ui/core/styles/createBreakpoints'

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    VZWhite: Palette['primary']
    VZBlack: Palette['primary']
    VZRed: Palette['primary']
  }
  interface PaletteOptions {
    VZWhite: PaletteOptions['primary']
    VZBlack: PaletteOptions['primary']
    VZRed: PaletteOptions['primary']
  }
}

const breakpoints = createBreakpoints({})
export const theme: Theme = createMuiTheme({
  overrides: {
    MuiContainer: {
      root: {
        paddingLeft: '64px',
        paddingRight: '64px'
      }
    },
    MuiTypography: {
      gutterBottom: {
        marginBottom: '27px'
      }
    },
    MuiFormHelperText: {
      root: {
        fontSize: '12px',
        color: '#000000',

        '&.Mui-error': {
          color: '#000000'
        },
        '&.Mui-success': {
          color: '#000000'
        }
      },
      contained: {
        marginLeft: '0'
      }
    },
    MuiInputBase: {
      root: {
        fontSize: '14px'
      },
      input: {
        background: '#ffffff',
        border: '1px solid #D8DADA'
      }
    },
    MuiFilledInput: {
      root: {
        backgroundColor: '#ffffff',

        '&.Mui-focused': {
          backgroundColor: '#ffffff'
        },
        '&:hover': {
          backgroundColor: '#ffffff'
        }
      },
      underline: {
        '&:before': {
          borderBottom: '1px solid #000000'
        },
        '&:hover:before': {
          borderBottom: '1px solid #000000'
        },
        '&:after': {
          borderBottom: '2px solid #000000'
        },
        '&:hover:after': {
          borderColor: '2px solid #000000'
        }
      },
      multiline: {
        padding: '0'
      },
      inputMultiline: {
        padding: '10px'
      }
    },
    MuiOutlinedInput: {
      root: {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#393939',
          borderWidth: '1px'
        }
      },
      input: {
        padding: '11.7px 14px'
      },
      notchedOutline: {
        '& span': {
          display: 'none'
        }
      }
    },
    MuiFormLabel: {
      root: {
        color: '#D8DADA',
        fontSize: '14px',

        '&.Mui-focused': {
          color: '#969797'
        }
      }
    },
    MuiInputLabel: {
      outlined: {
        transform: 'translate(14px, 13.5px) scale(1)',

        '&$shrink': {
          transform: 'translate(0, -14px) scale(0.75)'
        }
      }
    },
    MuiButton: {
      root: {
        borderRadius: '21px',
        fontFamily: '"Verizon-Bold"',

        fontSize: '14px',
        textTransform: 'none',
        padding: '10px 44px',
        margin: '44px 0'
      },
      outlined: {
        margin: '36px 0'
      },
      text: {
        margin: '36px 0'
      },
      contained: {
        backgroundColor: '#000000',
        color: '#ffffff'
      }
    }
  },
  typography: {
    fontFamily: `"Verizon-Regular"`,
    h1: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '4.5rem',
      lineHeight: 'calc(4.5rem * .95)',
      [breakpoints.down('lg')]: {
        fontSize: '4rem',
        lineHeight: 'calc(4rem * .95)'
      },
      [breakpoints.down('md')]: {
        fontSize: '3.5rem',
        lineHeight: 'calc(3.5rem * .95)'
      },
      [breakpoints.down('sm')]: {
        fontSize: '2.5rem',
        lineHeight: 'calc(2.5rem * .95)'
      }
    },
    h2: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '3.75rem',
      lineHeight: 'calc(3.75rem * .95)',
      [breakpoints.down('md')]: {
        fontSize: '3rem',
        lineHeight: 'calc(3rem * .95)'
      },
      [breakpoints.down('sm')]: {
        fontSize: '2.5rem',
        lineHeight: 'calc(2.5rem * .95)'
      }
    },
    h3: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '3rem',
      lineHeight: 'calc(3rem * .95)',
      [breakpoints.down('lg')]: {
        fontSize: '2.25rem',
        lineHeight: 'calc(2.25rem * .95)'
      },
      [breakpoints.down('md')]: {
        fontSize: '2rem',
        lineHeight: 'calc(2rem * .95)'
      },
      [breakpoints.down('sm')]: {
        fontSize: '1.75rem',
        lineHeight: 'calc(1.75rem * .95)'
      }
    },
    h4: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '2.25rem',
      lineHeight: 'calc(2.25rem * .95)',
      [breakpoints.down('lg')]: {
        fontSize: '2rem',
        lineHeight: 'calc(2rem * .95)'
      },
      [breakpoints.down('md')]: {
        fontSize: '1.75rem',
        lineHeight: 'calc(1.75rem * .95)'
      },
      [breakpoints.down('sm')]: {
        fontSize: '1.5rem',
        lineHeight: 'calc(1.5rem * .95)'
      }
    },
    h5: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '1.5rem',
      lineHeight: 'calc(1.5rem * .95)'
    },
    h6: {
      fontFamily: '"Verizon-Bold"',
      color: '#000',
      fontSize: '1.25rem',
      lineHeight: 'calc(1.25rem * .95)'
    },
    body1: {
      fontFamily: '"Verizon-Regular"',
      color: '#000',
      fontSize: '1.125rem',
      lineHeight: '1.25rem',
      [breakpoints.down('xs')]: {
        fontSize: '1rem',
        lineHeight: '1.125rem'
      }
    },
    body2: {
      fontFamily: '"Verizon-Regular"',
      color: '#000',
      fontSize: '1rem',
      lineHeight: '1.125rem'
    }
  },
  palette: {
    VZWhite: {
      main: '#FFF'
    },
    VZBlack: {
      main: '#000000'
    },
    VZRed: {
      main: '#FF1200'
    },
    primary: {
      light: '#FFF',
      main: '#000',
      dark: '#000'
    },
    secondary: {
      main: '#FF1200'
    },
    error: {
      main: '#ED7000'
    },
    success: {
      main: '#00AC3E'
    }
  }
})
